import React, { useState } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { FILE_BASE_URL } from '../../config';

interface DocumentInfoAndFilesProps {
  documentData?: any; // Có thể là một văn bản hoặc mảng văn bản
  showPreview?: boolean;
  onFileClick?: (file: any) => void;
  showOnlyFiles?: boolean; // Chỉ hiển thị file đính kèm
  showOnlyInfo?: boolean;  // Chỉ hiển thị thông tin văn bản
  hideHeader?: boolean;    // Ẩn header mặc định
}

const DocumentInfoAndFiles: React.FC<DocumentInfoAndFilesProps> = ({
  documentData,
  showPreview = true,
  onFileClick,
  showOnlyFiles = false,
  showOnlyInfo = false,
  hideHeader = false
}) => {
  const [selectedFilePreview, setSelectedFilePreview] = useState<string | null>(null);

  // Hàm format file path
  const formatFilePath = (filePath: string): string => {
    if (!filePath) return "";
    // Remove leading slashes
    const cleanPath = filePath.replace(/^\/+/, "");
    return `${FILE_BASE_URL}/${cleanPath}`;
  };

  // Hàm xử lý khi click vào file
  const handleFileClick = (file: any) => {
    // Ưu tiên fileview_active nếu có, sau đó mới đến preview.file_path, cuối cùng mới là file_path
    let filePathToUse: string | undefined;
    
    if (file.fileview_active && file.fileview_active.trim() !== '') {
      filePathToUse = file.fileview_active;
    } else if (file.preview?.file_path) {
      filePathToUse = file.preview.file_path;
    } else {
      filePathToUse = file.file_path || file.filePath;
    }
    
    const filePath = formatFilePath(filePathToUse || '');
    setSelectedFilePreview(filePath);
    
    // Gọi callback nếu có
    if (onFileClick) {
      onFileClick(file);
    }
  };

  // Hàm format file size
  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Hàm sắp xếp files theo thứ tự: is_active_qlvb=1 > file chính đã ký số > file chính chưa ký > các file khác
  const sortFiles = (files: any[]): any[] => {
    if (!files || files.length === 0) return files;
    
    return [...files].sort((a, b) => {
      // Kiểm tra is_active_qlvb = 1 (ưu tiên cao nhất)
      const aIsActiveQlvb = a.is_active_qlvb === 1 || a.is_active_qlvb === true;
      const bIsActiveQlvb = b.is_active_qlvb === 1 || b.is_active_qlvb === true;
      
      // 0. File có is_active_qlvb = 1 (ưu tiên cao nhất)
      if (aIsActiveQlvb && !bIsActiveQlvb) return -1;
      if (bIsActiveQlvb && !aIsActiveQlvb) return 1;
      
      // Kiểm tra file chính
      const aIsMain = a.file_type_category === 'main';
      const bIsMain = b.file_type_category === 'main';
      
      // Kiểm tra file đã ký số (cho văn bản đi)
      const aIsSigned = a.is_signed === true || 
                        (a.fileview_active && a.fileview_active.trim() !== '');
      const bIsSigned = b.is_signed === true || 
                        (b.fileview_active && b.fileview_active.trim() !== '');
      
      // 1. File chính đã ký số (ưu tiên cao thứ hai)
      if (aIsMain && aIsSigned && !(bIsMain && bIsSigned)) return -1;
      if (bIsMain && bIsSigned && !(aIsMain && aIsSigned)) return 1;
      
      // 2. File chính chưa ký số (ưu tiên thứ ba)
      if (aIsMain && !aIsSigned && !(bIsMain && bIsSigned)) {
        if (!bIsMain) return -1;
        if (bIsMain && bIsSigned) return 1;
        return 0; // Cả hai đều là main và chưa ký, giữ nguyên thứ tự
      }
      if (bIsMain && !bIsSigned && !(aIsMain && aIsSigned)) {
        if (!aIsMain) return 1;
        if (aIsMain && aIsSigned) return -1;
        return 0; // Cả hai đều là main và chưa ký, giữ nguyên thứ tự
      }
      
      // 3. Các file còn lại (ưu tiên thấp nhất)
      return 0;
    });
  };



  // Kiểm tra xem có phải nhiều văn bản không
  const isMultipleDocuments = Array.isArray(documentData);
  const documents = isMultipleDocuments ? documentData : (documentData ? [documentData] : []);

  // Render thông tin văn bản
  const renderDocumentInfo = () => (
    <Box sx={{ 
      p: 1, 
      backgroundColor: '#f8f9fa', 
      border: '1px solid #e0e0e0', 
      borderRadius: 1 
    }}>
      {!hideHeader && (
        <Typography variant="h6" sx={{ 
          mb: 2, 
          fontWeight: 'bold', 
          color: '#1976d2',
          borderBottom: '2px solid #1976d2',
          pb: 1
        }}>
          {isMultipleDocuments ? 'Danh sách văn bản' : 'Thông tin văn bản'}
        </Typography>
      )}
      
      {isMultipleDocuments ? (
        // Hiển thị danh sách văn bản
        <Box>
          {documents.map((doc: any, index: number) => (
            <Box key={doc?.id || index} sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ 
                fontSize: '13px',
                mb: 1
              }}>
                {index + 1}. {`Trích yếu: ${doc?.description || doc?.title || doc?.trich_yeu || 'Không có trích yếu'}`}
              </Typography>
             
            </Box>
          ))}
        </Box>
      ) : (
        // Hiển thị thông tin một văn bản (giữ nguyên logic cũ)
        <Box sx={{display: 'flex', alignItems: 'center' }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 'bold', minWidth: '100px' }}>
            Trích yếu:
          </Typography>
          <Typography variant="body2" sx={{ flex: 1 }}>
            {documentData?.description || documentData?.trich_yeu || documentData?.title || 'Không có thông tin'}
          </Typography>
        </Box>
      )}
    </Box>
  );

  // Render file đính kèm (chỉ hiển thị khi không phải nhiều văn bản)
  const renderFiles = () => {
    // Ẩn file đính kèm khi có nhiều văn bản
    if (isMultipleDocuments) {
      return <><Typography variant="h6" sx={{ 
        mb: 2, 
        fontWeight: 'bold', 
        color: '#1976d2',
        borderBottom: '2px solid #1976d2',
        pb: 1
      }}>File đính kèm</Typography></>;
    }

    return (
    <Box sx={{ 
      flex: 1, 
      p: 2,
      display: 'flex',
      flexDirection: 'column'
    }}>
      <Typography variant="h6" sx={{ 
        mb: 2, 
        fontWeight: 'bold', 
        color: '#1976d2',
        borderBottom: '2px solid #1976d2',
        pb: 1
      }}>
        File đính kèm
      </Typography>
      
      <Box sx={{ 
        flex: 1,
        overflow: 'auto',               
      }}>
        {documentData && documentData.files && documentData.files.length > 0 ? (
          <Box sx={{ width: '100%', maxWidth: '100%', overflow: 'hidden' }}>
            {sortFiles(documentData.files).map((file: any, index: number) => {
              const fileName = file.file_name || file.fileName || file.name || `File ${index + 1}`;
              const fileSize = file.file_size ? formatFileSize(file.file_size) : file.fileSize ? formatFileSize(file.fileSize) : '';
              return (
                <Box
                  key={index}
                  onClick={() => handleFileClick(file)}
                  sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    mb: 1,
                    color: 'primary.main',
                    cursor: 'pointer',
                    textDecoration: 'underline',
                    fontSize: '13px',
                    minWidth: 0,
                    width: '100%',
                    maxWidth: '100%',
                    overflow: 'hidden',
                    boxSizing: 'border-box'
                  }}
                >
                  <Box
                    sx={{
                      flex: '1 1 0%',
                      minWidth: 0,
                      width: 0, // Đặt width: 0 để flex có thể shrink element
                      maxWidth: 'calc(100% - 80px)', // Trừ đi không gian cho file size
                      overflow: 'hidden',
                      display: 'flex',
                      alignItems: 'center'
                    }}
                  >
                    <Typography 
                      variant="body2" 
                      component="span"
                      sx={{ 
                        fontSize: '13px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        display: 'block',
                        width: '100%',
                        maxWidth: '100%',
                        minWidth: 0
                      }}
                      title={fileName}
                    >
                      {fileName}
                    </Typography>
                  </Box>
                  <Typography 
                    variant="caption" 
                    sx={{ 
                      color: '#666', 
                      ml: 1,
                      flexShrink: 0,
                      flexGrow: 0,
                      whiteSpace: 'nowrap',
                      display: 'inline-block'
                    }}
                  >
                    {fileSize}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        ) : (
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            height: '100%',
            p: 2
          }}>
            <Typography variant="body2" sx={{ color: '#666', textAlign: 'center' }}>
              Không có file đính kèm
            </Typography>
          </Box>
        )}
      </Box>
      
      {/* File Preview Popup */}
      <Dialog
        open={showPreview && !!selectedFilePreview}
        onClose={() => setSelectedFilePreview(null)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 1,
            boxShadow: '0 4px 20px rgba(0,0,0,0.15)'
          }
        }}
      >
        <DialogTitle sx={{ 
          backgroundColor: '#086428',
          color: 'white',
          fontWeight: 500,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          height: '51px',
          padding: '12px 16px',
          boxSizing: 'border-box'
        }}>
          Xem trước file
          <IconButton
            aria-label="close"
            onClick={() => setSelectedFilePreview(null)}
            sx={{
              color: 'white',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.1)'
              }
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers sx={{ p: 0, height: '70vh' }}>
          {selectedFilePreview && (() => {
            const filePath = selectedFilePreview;
            const lower = filePath.toLowerCase();
            
            if (lower.match(/\.(jpg|jpeg|png|gif|bmp|webp)$/)) {
              return (
                <img 
                  src={filePath} 
                  alt="Preview" 
                  style={{ 
                    width: '100%', 
                    height: '100%',
                    objectFit: 'contain'
                  }} 
                />
              );
            } else if (lower.endsWith('.pdf')) {
              return (
                <iframe 
                  src={filePath + '#zoom=80'} 
                  width="100%" 
                  height="100%" 
                  title="PDF Preview"
                  style={{ border: 'none' }}
                />
              );
            } else {
              return (
                <Box sx={{ 
                  display: 'flex', 
                  flexDirection: 'column',
                  alignItems: 'center', 
                  justifyContent: 'center',
                  height: '100%',
                  p: 4
                }}>
                  <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
                    Không thể xem trước file này
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    File type: {filePath.split('.').pop()?.toUpperCase() || 'Unknown'}
                  </Typography>
                  <Button
                    variant="contained"
                    onClick={() => window.open(filePath, '_blank')}
                    sx={{ minWidth: '120px' }}
                  >
                    Mở file
                  </Button>
                </Box>
              );
            }
          })()}
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setSelectedFilePreview(null)}>
            Đóng
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
    );
  };

  // Render dựa trên props
  if (showOnlyInfo) {
    return renderDocumentInfo();
  }

  if (showOnlyFiles) {
    const filesComponent = renderFiles();
    // // Nếu có nhiều văn bản, không hiển thị file đính kèm
    // if (isMultipleDocuments) {
    //   return null;
    // }
    return filesComponent;
  }

  // Render mặc định (cả hai)
  return (
    <Box sx={{ display: 'flex', height: '100%' }}>
      {/* Bên trái - Thông tin văn bản */}
      <Box sx={{ 
        flex: 1, 
        p: 2, 
        borderRight: '1px solid #e0e0e0',
        display: 'flex',
        flexDirection: 'column',
        gap: 2
      }}>
        {renderDocumentInfo()}
      </Box>

      {/* Bên phải - Danh sách file đính kèm */}
      {renderFiles()}
    </Box>
  );
};

export default DocumentInfoAndFiles;
